'use client'

import React, { useEffect, useRef, useState } from 'react';
import { IoPlayCircle, IoPauseCircle, IoVolumeHigh, IoVolumeMute } from 'react-icons/io5';

interface AudioPlayerProps {
  audioUrl: string;
  songTitle: string;
  artist?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, songTitle, artist = "John Danziger" }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [seekHoverWidth, setSeekHoverWidth] = useState(0);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const setAudioData = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };

    const setAudioTime = () => setCurrentTime(audio.currentTime);

    audio.addEventListener('loadeddata', setAudioData);
    audio.addEventListener('timeupdate', setAudioTime);

    return () => {
      audio.removeEventListener('loadeddata', setAudioData);
      audio.removeEventListener('timeupdate', setAudioTime);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    setIsMuted(newVolume === 0);
  };

  const toggleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.volume = volume;
      setIsMuted(false);
    } else {
      audioRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progress = progressRef.current;
    if (!audio || !progress) return;

    const percent = e.nativeEvent.offsetX / progress.offsetWidth;
    audio.currentTime = percent * audio.duration;
  };

  const handleSeekHover = (e: React.MouseEvent<HTMLDivElement>) => {
    const progress = progressRef.current;
    if (!progress) return;

    const hoverWidth = (e.nativeEvent.offsetX / progress.offsetWidth) * 100;
    setSeekHoverWidth(hoverWidth);
  };

  return (
    <div className="bg-black border border-gray-800 rounded-lg p-4 sm:p-6 shadow-lg">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
        <div className="mb-2 sm:mb-0">
          <h3 className="text-lg sm:text-xl text-white font-semibold">{songTitle}</h3>
          <p className="text-xs sm:text-sm text-gray-400">{artist}</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-4">
          <button onClick={toggleMute} className="text-white hover:text-gray-300 transition-colors duration-300">
            {isMuted ? <IoVolumeMute size={20} /> : <IoVolumeHigh size={20} />}
          </button>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={isMuted ? 0 : volume}
            onChange={handleVolumeChange}
            className="w-16 sm:w-24"
          />
        </div>
      </div>
      <div 
        ref={progressRef}
        className="h-2 bg-gray-700 rounded-full mb-4 cursor-pointer relative"
        onClick={handleSeek}
        onMouseMove={handleSeekHover}
        onMouseLeave={() => setSeekHoverWidth(0)}
      >
        <div 
          className="h-full bg-white rounded-full absolute top-0 left-0"
          style={{ width: `${(currentTime / duration) * 100}%` }}
        />
        <div 
          className="h-full bg-gray-500 rounded-full absolute top-0 left-0 opacity-50"
          style={{ width: `${seekHoverWidth}%` }}
        />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs sm:text-sm text-gray-400">{formatTime(currentTime)}</span>
        <button
          onClick={togglePlayPause}
          className="text-white hover:text-gray-300 transition-colors duration-300"
        >
          {isPlaying ? <IoPauseCircle size={36} /> : <IoPlayCircle size={36} />}
        </button>
        <span className="text-xs sm:text-sm text-gray-400">{formatTime(duration)}</span>
      </div>
      <audio ref={audioRef} src={audioUrl} />
    </div>
  );
};

export default AudioPlayer;